import React, { useState, useMemo, useContext } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Column, useTable, useSortBy } from "react-table";
import {
  Tenant,
  Account,
  Site,
  CategoryTree,
} from "pages/Provision/Provision.type";
import { FetchSiteByOperationsContext } from "pages/Provision/Provision";
import Table from "pages/Provision/tables/Table";

import Modal from "react-modal";
import AddTreeForm from "pages/Provision/forms/TreeForm";
import EditTreeForm from "pages/Provision/forms/EditTreeForm";

import { FiEdit2 } from "react-icons/fi";
import { FaMagnifyingGlass, FaRegTrashCan } from "react-icons/fa6";
import "pages/Provision/Provision.css";

type SiteTableProps = {
  tenant: Tenant;
  account: Account;
  site: Site;
  trees: CategoryTree[];
};

type columnData = {
  accountResName: string;
  siteResName: string;
  categoryTreeResName: string;
  categoryTreeName: string;
  categoryTreeStatus: string;
  categoryPaths: string;
};

const CategoryTreeTable: React.FC<SiteTableProps> = ({
  tenant,
  account,
  site,
  trees,
}) => {
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [editModalIsOpen, setEditModalIsOpen] = useState(false);
  const [deleteModalIsOpen, setDeleteModalIsOpen] = useState(false);
  const [selectedTree, setSelectedTree] = useState<CategoryTree>(null!);
  const [searchQuery, setSearchQuery] = useState("");
  const fetchSites = useContext(FetchSiteByOperationsContext);
  const navigate = useNavigate();

  const data = useMemo(
    () =>
      trees
        .filter((categoryTree) => categoryTree.siteResName === site.siteResName)
        .map((tree) => ({
          ...tree,
          categoryPaths: tree.categoryPathList
            .map((path) => path.categoryPath)
            .join(", "),
        }))
        .filter((tree) => {
          return (
            tree.categoryTreeResName
              .toLowerCase()
              .includes(searchQuery.toLowerCase()) ||
            tree.categoryTreeName
              .toLowerCase()
              .includes(searchQuery.toLowerCase()) ||
            tree.categoryPaths.toLowerCase().includes(searchQuery.toLowerCase())
          );
        }),
    [tenant, account, site, trees, searchQuery],
  );

  const columns: Column<columnData>[] = React.useMemo(
    () => [
      { Header: "CategoryTreeResName", accessor: "categoryTreeResName" },
      { Header: "CategoryTreeName", accessor: "categoryTreeName" },
      { Header: "Status", accessor: "categoryTreeStatus" },
      { Header: "CategoryPaths", accessor: "categoryPaths" },
      {
        Header: "Action",
        Cell: ({ row }: { row: any }) => (
          <div>
            <button
              onClick={() => navigate(`${row.original.categoryTreeResName}`)}
            >
              <FaMagnifyingGlass />
            </button>
            <button
              onClick={() => {
                setSelectedTree(
                  trees.filter(
                    (tree) =>
                      tree.categoryTreeResName ===
                      row.original.categoryTreeResName,
                  )[0],
                );
                setEditModalIsOpen(true);
              }}
            >
              <FiEdit2 />
            </button>
            <button
              onClick={() => {
                setSelectedTree(
                  trees.filter(
                    (tree) =>
                      tree.categoryTreeResName ===
                      row.original.categoryTreeResName,
                  )[0],
                );
                setDeleteModalIsOpen(true);
              }}
            >
              <FaRegTrashCan />
            </button>
          </div>
        ),
      },
    ],
    [tenant, account, site, trees],
  );

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } =
    useTable({ columns, data }, useSortBy);

  return (
    <div>
      <div className="header-container">
        <h1>Category Trees</h1>
        <button
          onClick={() => {
            setModalIsOpen(true);
          }}
        >
          + Tree
        </button>
      </div>

      <div>
        <nav aria-label="breadcrumb">
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <Link to="/scrappy/provision">Home</Link>
            </li>
            <li className="breadcrumb-item">
              <Link to={`/scrappy/provision/${tenant.customerResName}`}>
                {tenant.customerName}
              </Link>
            </li>
            <li className="breadcrumb-item">
              <Link
                to={`/scrappy/provision/${tenant.customerResName}/${account.accountResName}`}
              >
                {account.accountName}
              </Link>
            </li>
            <li className="breadcrumb-item active" aria-current="page">
              {site.siteName}
            </li>
            <li className="breadcrumb-item active" aria-current="page">
              Trees
            </li>
          </ol>
        </nav>
      </div>

      <Modal
        isOpen={modalIsOpen}
        onRequestClose={() => setModalIsOpen(false)}
        className="modal-content"
        ariaHideApp={false}
      >
        <AddTreeForm account={account.accountResName} site={site.siteResName} />
      </Modal>

      <Modal
        isOpen={editModalIsOpen}
        onRequestClose={() => setEditModalIsOpen(false)}
        className="modal-content"
        ariaHideApp={false}
      >
        <EditTreeForm edit={true} selectedTree={selectedTree} />
      </Modal>

      <Modal
        isOpen={deleteModalIsOpen}
        onRequestClose={() => setDeleteModalIsOpen(false)}
        className="modal-content"
        ariaHideApp={false}
      >
        <EditTreeForm edit={false} selectedTree={selectedTree} />
      </Modal>

      <div className="table-header-container">
        <input
          type="text"
          placeholder="Search..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
        <button onClick={() => setSearchQuery("")}>Clear</button>
        <button
          className="button-end"
          onClick={() =>
            fetchSites(account.accountResName, site.siteResName, [
              "category-trees",
            ])
          }
        >
          Refresh
        </button>
      </div>
      <Table
        getTableProps={getTableProps}
        getTableBodyProps={getTableBodyProps}
        headerGroups={headerGroups}
        rows={rows}
        prepareRow={prepareRow}
      />
    </div>
  );
};

export default CategoryTreeTable;
